import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Empty, Select, Spin, Tooltip} from 'antd';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import {useInfinite} from '@/utils/createInfiniteHook';
import useCrud from '@/hooks/useCrud';
import {useTranslation} from 'react-i18next';
import createSearchService from '@/utils/createSearchService';
import {InFilter, TermFilter} from '@dofleini/query-builder';
import uniqBy from 'lodash/uniqBy';
import {LoadingOutlined} from '@ant-design/icons';
import {isEmpty} from 'lodash';
import {useQuery, useQueryClient} from 'react-query';

const createOption = (name, title, idField = '_id') => (item) => ({
  value: item?.[idField],
  _id: item?.[idField],
  id: item?.[idField],
  title: item[title],
  key: item?.[idField],
  label: item[name],
  name: item[name],
  space: item['space']
});

const createAllOption = (option) => ({
  value: option?.value,
  _id: option?.value,
  id: option?.value,
  title: option?.label,
  key: option?.value,
  label: option?.label,
  name: option?.label
});

const updateSelectedValue = (isMultiple, value, options, allOption) => (prevState) => {
  if (isMultiple) {
    if (allOption?.show) {
      if (value?.includes(allOption?.value) && !prevState?.find(prev => prev?.value === allOption?.value)) {
        return [createAllOption(allOption)];
      }
    }

    return value?.map(item => {
      const option = prevState?.find(prev => prev?.value === item);
      if (!option) {
        return options?.find(prev => prev?.value === item);
      }
      if (allOption?.show && item === allOption?.value) {
        return null;
      }
      return option;
    }).filter(v => v);
  }

  return value ? options?.find(prev => prev?.value === value) : null;
};

const useFindSelectedValue = (value, options, _id, name, title, queryKey, service, allOption, enabled, useProjections = true, projections, objectId = true) => {
  const [filters, size, result] = useMemo(() => {
    const filter = {field: _id, value, objectId};
    if (value) {
      if (Array.isArray(value)) {
        if(value.includes(allOption?.value)){
          return [null, null, value];
        }
        if(value.some(v => v?.[_id] && v?.[name] && v?.[title]))
          return [null, null, value];
        return [new InFilter(filter), value?.length];
      } else {
        if(value?.[_id] && value?.[name] && value?.[title])
          return [null, null, [value]];
        return [new TermFilter(filter), 1];
      }
    }

    return [null, null, null];
  }, [_id, value, objectId, allOption?.value, name, title]);

  const {data, isLoading, ...rest} = useQuery([queryKey, 'find-selected-value', filters], () => service?.({
    size,
    filters,
    projections: useProjections ? {[_id]: 1, [title]: 1, [name]: 1, space: 1, ...projections} : undefined,
    useProjections
  }), {enabled: enabled && size > 0});

  return {selectedData: data?.data || result, loadingSelectedValue: isLoading, ...rest};
};

const antIcon = <LoadingOutlined style={{fontSize: 24}} spin/>;

const {Option} = Select;
const AsyncSelect = ({
  queryKey,
  service,
  value,
  onChange,
  ignoreValue,
  nameToShow,
  titleToShow,
  customMethod,
  filters,
  afterLoadData,
  additionalParams,
  extraOptionData, // handle the extra information in the option for the select
  translation,
  canAdd,
  identifyValue,
  additionalData,
  mode,
  style,
  allOption,
  alwaysAdd,
  searchValue,
  defaultValue,
  selectedEntity,
  queryConfig,
  onBlur,
  loadValueWithoutProjections,
  allowClear,
  deferFetchUntilOpen,
  objectId = true,
  ...props
}) => {
  const {t} = useTranslation(translation || 'common');
  const name = nameToShow ? nameToShow : 'name';
  const title = titleToShow ? titleToShow : name;
  const _id = identifyValue ? identifyValue : '_id';
  const [search, setSearch] = useState('');
  const [lastOptions, setLastOptions] = useState([]);
  const [data, setData] = useState([]);
  const {create} = useCrud(service, queryKey);
  const inputRef = useRef();
  const queryClient = useQueryClient();
  const [isMultiple, emptyValue] = useMemo(() => {
    const isMultiple = mode === 'multiple';

    return [isMultiple, isMultiple ? [] : null];
  }, [mode]);
  const [selectedValue, setSelectedValue] = useState(emptyValue);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (dropdownOpen || (search?.trim()?.length > 0)) {
      setHasOpened(true);
    }
  }, [dropdownOpen, search]);

  useEffect(() => {
    setHasOpened(false);
  }, [filters, queryKey]);

  const onSearch = useCallback((val) => {
    queryClient?.removeQueries(queryKey);
    setSearch(val);
    searchValue && searchValue(val);
  }, [queryClient, queryKey, searchValue]);

  const searchService = useMemo(() => createSearchService(customMethod || service.search), [customMethod, service.search]);

  const enabledQuery = useMemo(() => {
    if (selectedValue === emptyValue) return true;

    if (Array.isArray(value)) {
      const selectedIds = Array.isArray(selectedValue) ? selectedValue?.map(o => o?._id): [selectedValue?._id];
      for (let i = 0; i < value?.length; i++) {
        if (!selectedIds?.includes(value?.[i]?.[_id] || value?.[i])) {
          return true;
        }
      }
      return false;
    }

    return (value?.[_id] || value) !== (selectedValue?._id || selectedValue);
  }, [emptyValue, selectedValue, value, _id]);

  const {
    selectedData,
    loadingSelectedValue
  } = useFindSelectedValue(value, data, _id, name, title, queryKey, searchService, allOption, enabledQuery, !loadValueWithoutProjections, additionalParams?.projections, objectId);

  const clearValue = useCallback(() => (
    setSelectedValue(emptyValue)
  ), [emptyValue]);

  useEffect(() => {
    if (selectedValue && (!value || (Array.isArray(value) && !value?.length))) {
      clearValue();
    }
  }, [clearValue, selectedValue, value]);

  useEffect(() => {
    if (value && typeof value === 'string' && data?.length && value !== selectedValue?._id) {
      const options = data?.find(o => o?.[_id] === value);
      options && setSelectedValue(createOption(name, title, _id)(options));
    }
  }, [data, name, selectedValue?._id, title, value, _id]);

  useEffect(() => {
    if (selectedData?.length) {
      if (isMultiple) {
        setSelectedValue(selectedData.includes(allOption?.value) ?
          [createAllOption(allOption)] :
          selectedData?.map(createOption(name, title, _id))
        );
      } else {
        setSelectedValue(createOption(name, title, _id)(selectedData[0]) || emptyValue);
      }
    }
    else {
      if (selectedData === null){
        clearValue();
      }
    }
  }, [allOption, clearValue, mode, name, selectedData, title, isMultiple, emptyValue, _id]);

  const beforeJoin = useCallback(async (pages) => {
    let data = null;
    let joint = [];

    pages?.forEach(({data}) => (joint = joint.concat(data?.filter(item => (item?.[_id] || item) !== allOption?.value)))); // Iterate all pages
    joint = joint?.length && allOption?.show ? [createAllOption(allOption), ...joint] : joint;

    if (value && !isEmpty(selectedEntity)) {
      if (!joint.some(e => e?.[_id] === selectedEntity?.[_id])) {
        (!search || selectedEntity?.[title]?.toLowerCase()?.includes(search.toLowerCase())) && joint.unshift(selectedEntity);
      }
      setData(joint);
    } else {
      setData(data ? uniqBy(data, _id) : joint?.filter(x => x?._id !== _id));
    }
  }, [allOption, value, selectedEntity, _id, search, title]);

  // Compute effective enabled flag: when deferFetchUntilOpen is true, do not fetch until the dropdown is opened or the user types something
  const effectiveEnabled = useMemo(() => {
    const baseEnabled = (queryConfig?.enabled ?? true);
    if (!deferFetchUntilOpen) return baseEnabled;
    return baseEnabled && (hasOpened || dropdownOpen || (search?.trim()?.length > 0));
  }, [deferFetchUntilOpen, dropdownOpen, hasOpened, queryConfig?.enabled, search]);

  let {isLoading, isFetching, fetchNextPage, hasNextPage} = useInfinite({
    key: [queryKey, filters, search?.trim()],
    search: search?.trim(),
    filters,
    service: customMethod || service?.search,
    additionalParams,
    beforeJoin,
    queryConfig: {
      size: 50,
      ...queryConfig,
      enabled: effectiveEnabled
    },
    allOptionValue: allOption?.value
  });

  useEffect(() => {
    if (afterLoadData)
      afterLoadData(data);
  }, [afterLoadData, data]);

  const onScroll = useCallback((event) => {
    const target = event.target;
    if (!(isLoading || isFetching) && hasNextPage && target.scrollTop + target.offsetHeight >= (target.scrollHeight - 50)) {
      fetchNextPage().then();
    }
  }, [isLoading, isFetching, hasNextPage, fetchNextPage]);

  const options = useMemo(() => {
    let elements = data || null;
    if (elements) {
      if (ignoreValue) {
        if (typeof ignoreValue === 'string') {
          elements = elements.filter(elem => elem[_id] !== ignoreValue);
        } else if (Array.isArray(ignoreValue)) {
          elements = data.filter(elem => !ignoreValue.some(item => item === elem[_id]));
        }
      }
      const opts = elements.map(createOption(name, title, _id));
      setLastOptions(opts);
      return opts;
    }
    return [];
  }, [data, ignoreValue, name, title, _id]);

  const handlerBuildOptions = useCallback((item) => {
    const ExtraOptionData = extraOptionData;

    if (isLoading) return null;

    return <Option key={item.value} value={item.value} title={item.title}>
      <div>
        <Tooltip title={item.label || item.title}>
          {isLoading ? '' : item.title}
        </Tooltip>
        {extraOptionData && <ExtraOptionData data={item}/>}
      </div>
    </Option>;
  }, [extraOptionData, isLoading]);

  const customOnChange = useCallback((value, option) => {
    setSelectedValue(updateSelectedValue(isMultiple, value, options, allOption));

    if (isMultiple && allOption?.show && value?.includes(allOption?.value)) {
      if (value?.[value?.length - 1] === allOption?.value) {
        onChange && onChange([allOption?.value]);
      } else {
        onChange && onChange(value?.filter(x => x !== allOption?.value), option);
      }
    } else {
      onChange && onChange(value || null, option);
    }
  }, [allOption, isMultiple, onChange, options]);

  const onCreate = useCallback(async (value) => {
    try {
      const {data} = await create({[name]: value, ...additionalData});
      let ids;
      setSelectedValue(prevState => {
        let result;
        if (isMultiple) {
          result = [...prevState, createOption(name, title, _id)(data)];
          ids = result.map(x => x?._id);
        } else {
          result = createOption(name, title, _id)(data);
          ids = result?._id;
        }
        return result;
      });
      setData(prevState => [data, ...prevState]);
      onChange && onChange(ids);
      inputRef.current?.blur();
    } catch (e) {
      console.log(e);
    }
  }, [create, name, additionalData, onChange, isMultiple, title, _id]);

  const addCmp = useMemo(() => {
    return (
      <div className="cursor-pointer" onClick={() => onCreate(search)}>
        <span className="mr-1">{t('common:add')}</span>
        <span className="text-primary font-bold">{search}</span>
      </div>
    );
  }, [onCreate, search, t]);

  const handleBlur = useCallback((e) => {
    setSearch('');
    onBlur && onBlur(e);
  }, [onBlur]);

  useEffect(() => {
    return () => {
      queryClient?.removeQueries(queryKey);
    };
  }, [queryClient, queryKey]);

  return (
    <Select
      ref={inputRef}
      optionFilterProp={'title'}
      showSearch
      filterOption={false}
      defaultValue={defaultValue}
      allowClear={allowClear}
      mode={mode}
      onDropdownVisibleChange={setDropdownOpen}
      notFoundContent={<>
        {(canAdd && search) || (alwaysAdd && search) ? addCmp :
          ((isLoading || isFetching) ?
            <div className="flex items-center justify-center"><Spin size="small" indicator={antIcon}/></div> :
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>)}
      </>}
      {...props}
      onSearch={debounce(onSearch, 250)}
      loading={isLoading || isFetching || loadingSelectedValue}
      value={selectedValue}
      onChange={customOnChange}
      onPopupScroll={onScroll}
      onBlur={handleBlur}
      style={style ? style : {minWidth: '250px'}}
      dropdownRender={(menu) => (
        <>
          {menu}
          {(alwaysAdd && search && !isFetching && options.length) ? addCmp : null}
        </>

      )}
    >
      {!isFetching && !isLoading ?
        options.map(handlerBuildOptions) : [
          ...lastOptions.map(handlerBuildOptions),
          <Option key="loading" value="loading-item" disabled>
            <div key="loading" className="flex items-center justify-center w-full">
              <span className="mr-4"><Spin size="small" indicator={antIcon}/></span>
              {t('common:loading')}
            </div>
          </Option>
        ]}
    </Select>
  );

};

export default memo(AsyncSelect);

AsyncSelect.propTypes = {
  additionalParams: PropTypes.object,
  customMethod: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  filters: PropTypes.object,
  ignoreValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array
  ]),
  mode: PropTypes.string,
  nameToShow: PropTypes.string,
  titleToShow: PropTypes.string,
  canAdd: PropTypes.bool,
  translation: PropTypes.string,
  onChange: PropTypes.func,
  afterLoadData: PropTypes.func,
  queryKey: PropTypes.string,
  identifyValue: PropTypes.string,
  additionalData: PropTypes.object,
  extraOptionData: PropTypes.any,
  service: PropTypes.object,
  value: PropTypes.any,
  style: PropTypes.object,
  allOption: PropTypes.object,
  searchValue: PropTypes.func,
  alwaysAdd: PropTypes.bool,
  defaultValue: PropTypes.any,
  selectedEntity: PropTypes.object,
  queryConfig: PropTypes.object,
  onBlur: PropTypes.func,
  loadValueWithoutProjections: PropTypes.bool,
  allowClear: PropTypes.bool,
  deferFetchUntilOpen: PropTypes.bool,
  objectId: PropTypes.bool,
};

AsyncSelect.defaultProps = {
  filters: {},
  additionalData: {},
  allowClear: true,
  // By default keep previous behavior (fetch on mount). Set true to delay requests until dropdown is opened or user types.
  deferFetchUntilOpen: false
};
